/**
 * 원격 플레이어 아바타 — 보간 재생, 이름표, 시선/손전등 표시.
 * 서버 스냅샷은 15Hz 라서 INTERP_DELAY_MS 만큼 과거를 재생해 끊김 없이 보여준다.
 */

import * as THREE from 'three';
import { LIGHT_SLOTS } from './budget';
import { park } from './lightPool';
import { INTERP_DELAY_MS, type PlayerState } from '../../shared/protocol';
import { FLAG_CROUCH, FLAG_LIGHT } from './player';
import type { Net } from './net';
import { buildAvatar } from './avatar';
import { ep as epData } from './episodeData';

interface Avatar {
  /** 지금 이 몸이 어느 배역으로 세워졌나. 바뀌면 다시 세운다 */
  castId: string | null;
  group: THREE.Group;
  head: THREE.Group;
  label: THREE.Sprite;
  /** 손전등 자리(빛은 아니다 — 가까운 몇 명만 풀 슬롯이 빛을 준다) */
  lamp: THREE.Object3D;
  lightOn: boolean;
  held: THREE.Mesh;
  /** 아바타가 자기 애니메이션을 소유한다 — avatar.ts 의 update */
  update: (dt: number, speed: number, pitch: number, crouch: boolean) => void;
  color: string;
  name: string;
}

function labelTexture(name: string, color: string) {
  const cv = document.createElement('canvas');
  cv.width = 320;
  cv.height = 80;
  const ctx = cv.getContext('2d')!;
  ctx.fillStyle = 'rgba(8,10,14,0.72)';
  roundRect(ctx, 6, 14, 308, 52, 12);
  ctx.fill();
  ctx.strokeStyle = color;
  ctx.lineWidth = 3;
  roundRect(ctx, 6, 14, 308, 52, 12);
  ctx.stroke();
  ctx.font = 'bold 34px "Pretendard","Noto Sans KR",system-ui,sans-serif';
  ctx.fillStyle = '#f2f5fa';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(name.slice(0, 12), 160, 42);
  const tex = new THREE.CanvasTexture(cv);
  tex.colorSpace = THREE.SRGBColorSpace;
  return tex;
}

function roundRect(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

export class RemotePlayers {
  private avatars = new Map<string, Avatar>();
  private marks: { mesh: THREE.Group; until: number }[] = [];
  /**
   * 남이 한 걸음 옮길 때마다 부른다. 눈 위 발자국이 여기 걸린다.
   *
   * **자국을 여기서 직접 찍지 않는다.** 눈밭인지 마룻바닥인지는 이 파일이
   * 모르고(에피소드마다 다르다), 그 판정은 `outside.footstep` 이 한다.
   */
  onStep: ((id: string, x: number, y: number, z: number, yaw: number) => void) | null = null;

  /**
   * **남의 손전등은 고정 2슬롯** (2026-09-26). 예전엔 들어오는 사람마다 PointLight 를 하나씩 씬에 넣었다 —
   * 조명 개수가 바뀌어 **누가 들어올 때마다 모든 재질이 다시 컴파일**됐고, 6명이면 픽셀마다 조명이 5개 늘었다.
   * 맥북 M2 Pro 에서 점광·스포트가 16개를 넘으면 프레임이 77→50 으로 절벽처럼 떨어진다(budget.ts)
   */
  private lampSlots: THREE.PointLight[] = [];
  constructor(
    private scene: THREE.Scene,
    private net: Net,
  ) {
    for (let i = 0; i < LIGHT_SLOTS.remoteLamp; i++) { const l = new THREE.PointLight('#fff2d0', 0, 5); l.name = 'remoteLampSlot'; park(l); scene.add(l); this.lampSlots.push(l); }
    net.on('players', () => this.sync());
    // **배역은 `world` 로 온다.** `players` 만 듣고 있어서 배역이 정해져도 몸을 다시 세우지
    // 않았다 — 배역을 고르고 들어가도 전원이 절차 캡슐이었고 모델은 로드조차 되지 않았다.
    // sync 는 castId 가 달라질 때만 다시 세우므로 world 가 자주 와도 비용이 없다.
    net.on('world', () => this.sync());
    net.on('mark', (m) => this.addMark(m));
  }

  private sync() {
    for (const [id, p] of this.net.players) {
      if (id === this.net.you) continue;
      const want = this.net.world.roles[id] ?? null;
      const have = this.avatars.get(id);
      if (have && have.castId !== want) {
        this.scene.remove(have.group);   // 배역이 정해졌다. 몸을 다시 세운다
        this.avatars.delete(id);
      }
      if (!this.avatars.has(id)) this.avatars.set(id, this.make(p));
    }
    for (const [id, a] of this.avatars) {
      if (!this.net.players.has(id)) {
        this.scene.remove(a.group);
        this.avatars.delete(id);
      }
    }
  }

  /** 이름표. 배역을 받았으면 「배역 (플레이어)」 */
  private labelFor(p: PlayerState): string {
    const castId = this.net.world.roles[p.id];
    const c = castId ? epData().cast.byId[castId] : null;
    return c ? `${c.name}` : p.name;
  }

  private make(p: PlayerState): Avatar {
    // 배역이 정해졌으면 그 사람의 몸으로 세운다. 아직이면 색깔만 다른 기본 몸.
    // 3D 모델은 avatar.ts 의 MODELS 표에 경로만 넣으면 여기서 통째로 갈린다
    const castId = this.net.world.roles[p.id] ?? null;
    const { group, head, held, update } = buildAvatar(castId, new THREE.Color(p.color));

    const label = new THREE.Sprite(
      new THREE.SpriteMaterial({ map: labelTexture(this.labelFor(p), p.color), depthTest: false, transparent: true }),
    );
    label.position.y = 2.0;
    label.scale.set(1.1, 0.28, 1);
    label.renderOrder = 999;
    group.add(label);

    const lamp = new THREE.Object3D();
    lamp.position.set(0, 1.3, -0.3);
    group.add(lamp);

    this.scene.add(group);
    return { group, head, label, lamp, lightOn: false, held, update, color: p.color, name: p.name, castId };
  }

  /** 사람마다 지난 프레임 자리 — 그림자를 다시 구울지 가른다 */
  private lastPose = new Map<string, number[]>();

  /**
   * @returns 이번 프레임에 **가까이서(30m) 움직인** 사람 수. main.ts 는 이게 0 보다 클 때만 그림자를 다시 굽는다 —
   *   예전엔 누가 접속해 있기만 하면 그림자 맵 3장을 초당 20번 구웠다(대본을 읽으며 가만히 있어도)
   */
  update(nowMs: number, dt = 1 / 60, viewer?: THREE.Vector3): number {
    const renderAt = nowMs - INTERP_DELAY_MS;
    let movedNear = 0;

    for (const [id, a] of this.avatars) {
      const buf = this.net.buffers.get(id);
      if (!buf || buf.length === 0) {
        a.group.visible = false;
        continue;
      }
      a.group.visible = true;

      // renderAt 을 감싸는 두 샘플 찾기
      let i = buf.length - 1;
      while (i > 0 && buf[i - 1].at > renderAt) i--;
      const b = buf[i];
      const a0 = buf[Math.max(0, i - 1)];
      const span = b.at - a0.at;
      const t = span > 1 ? Math.max(0, Math.min(1, (renderAt - a0.at) / span)) : 1;

      const p0 = a0.pose;
      const p1 = b.pose;
      const x = lerp(p0[1], p1[1], t);
      const y = lerp(p0[2], p1[2], t);
      const z = lerp(p0[3], p1[3], t);
      const yaw = lerpAngle(p0[4], p1[4], t);
      const pitch = lerp(p0[5], p1[5], t);
      const flags = p1[6];

      a.group.position.set(x, y, z);
      a.group.rotation.y = yaw;
      {
        const lp = this.lastPose.get(id);
        const crouchNow = (p1[6] & FLAG_CROUCH) !== 0 ? 1 : 0;
        if (!lp || Math.abs(lp[0] - x) + Math.abs(lp[1] - y) + Math.abs(lp[2] - z) > 0.002 || Math.abs(lp[3] - yaw) > 0.002 || crouchNow !== (lp[4] ?? 0)) {
          if (!viewer || viewer.distanceToSquared(a.group.position) < 30 * 30) movedNear++;
          this.lastPose.set(id, [x, y, z, yaw, crouchNow]);
        }
      }
      this.onStep?.(id, x, y, z, yaw);
      const crouch = (flags & FLAG_CROUCH) !== 0;
      // **속도는 보간에 쓰는 두 샘플에서 뽑는다.** 위치 차이를 프레임 시간으로 나누면
      // 프레임률에 따라 값이 흔들리는데, 이건 서버가 보낸 시간 간격이라 안정적이다.
      // 걷기/달리기를 섞는 기준이므로 흔들리면 발이 떨린다.
      const dist = Math.hypot(p1[1] - p0[1], p1[3] - p0[3]);
      const speed = span > 1 ? (dist / span) * 1000 : 0;
      // 애니메이션은 아바타가 소유한다 — 모델이면 클립을 섞고, 절차 아바타면 예전처럼
      // 머리를 돌리고 몸을 눌러 웅크린다 (avatar.ts 참고)
      a.update(dt, speed, pitch, crouch);
      a.lightOn = (flags & FLAG_LIGHT) !== 0;

      const items = Object.entries(this.net.world.objects).filter(([, s]) => s.taken && s.by === id);
      a.held.visible = items.length > 0;

      // 이름표는 항상 카메라를 본다 (Sprite 는 자동)
      // scale.y 로 누르는 것은 절차 아바타뿐이다 — 그때만 나눠서 되돌린다
      const squash = a.group.scale.y;
      a.label.position.y = (crouch ? 1.5 : 2.0) / (squash || 1);
    }

    // 손전등 켠 사람 중 가까운 둘에게 슬롯을 준다
    const lit = [...this.avatars.values()].filter((a) => a.lightOn && a.group.visible);
    const wp = new THREE.Vector3();
    if (viewer) lit.sort((a, b) => a.group.position.distanceToSquared(viewer) - b.group.position.distanceToSquared(viewer));
    this.lampSlots.forEach((slot, i) => {
      const a = lit[i];
      if (!a) { park(slot); return; }
      a.lamp.getWorldPosition(wp); slot.position.copy(wp); slot.intensity = 3.2;
    });

    // 핑 마커 수명 처리
    for (let i = this.marks.length - 1; i >= 0; i--) {
      const m = this.marks[i];
      const left = m.until - nowMs;
      if (left <= 0) {
        this.scene.remove(m.mesh);
        this.marks.splice(i, 1);
        continue;
      }
      m.mesh.rotation.y += 0.02;
      m.mesh.position.y += Math.sin(nowMs * 0.004) * 0.002;
      const s = Math.min(1, left / 400);
      m.mesh.scale.setScalar(s);
    }
    return movedNear;
  }

  private addMark(m: { color: string; x: number; y: number; z: number }) {
    const g = new THREE.Group();
    const ring = new THREE.Mesh(
      new THREE.TorusGeometry(0.22, 0.03, 8, 24),
      new THREE.MeshBasicMaterial({ color: m.color, transparent: true, opacity: 0.9 }),
    );
    ring.rotation.x = Math.PI / 2;
    g.add(ring);
    const spike = new THREE.Mesh(
      new THREE.ConeGeometry(0.09, 0.34, 10),
      new THREE.MeshBasicMaterial({ color: m.color }),
    );
    spike.position.y = 0.3;
    spike.rotation.x = Math.PI;
    g.add(spike);
    g.position.set(m.x, m.y + 0.05, m.z);
    this.scene.add(g);
    this.marks.push({ mesh: g, until: performance.now() + 4000 });
  }
}

function lerp(a: number, b: number, t: number) {
  return a + (b - a) * t;
}

function lerpAngle(a: number, b: number, t: number) {
  let d = ((b - a + Math.PI) % (Math.PI * 2)) - Math.PI;
  if (d < -Math.PI) d += Math.PI * 2;
  return a + d * t;
}
